import React from 'react';
import { useTheme } from '@mui/material';
import { useInputContext } from './InputContext';
import { Box, BoxProps } from '../../Box';
import { themePalette } from '../../../theme/theme.palette';

export type InputLabelProps = Omit<React.LabelHTMLAttributes<HTMLLabelElement>, keyof BoxProps> &
  Pick<BoxProps, 'visuallyHidden' | 'left'> & {
    /**  Whether the label should be raised up or not. Defaults to `true` */
    raised?: boolean;

    children: React.ReactNode;

    sx?: BoxProps['sx'];
  };

const InputLabel: React.FC<InputLabelProps> = ({ raised = true, sx = {}, ...rest }) => {
  const { invalid } = useInputContext();
  const theme = useTheme();
  const { componentTokens, typographyTokens } = themePalette(theme.palette.mode);

  const getLabelColor = () => {
    if (invalid) {
      return componentTokens.inputs.colors.error.text.inputText.resting;
    }
    if (raised) {
      return componentTokens.inputs.colors.normal.text.label.filled;
    }
    return componentTokens.inputs.colors.normal.text.inputText.resting;
  };

  return (
    <Box
      as="label"
      pointerEvents="none"
      fontSize={typographyTokens.fontSizes.sm}
      px={4}
      color={getLabelColor()}
      top={0}
      left={0}
      sx={sx}
      position="absolute"
      whiteSpace="nowrap"
      transformOrigin="center left"
      transform={raised ? 'translate(6px, 6px) scale(0.65)' : 'translate(0, 14px) scale(1)'}
      transition="transform 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms"
      fontWeight={
        raised ? typographyTokens.fontWeights.semibold : typographyTokens.fontWeights.regular
      }
      {...rest}
    />
  );
};

export default InputLabel;
